import { useMutation, useQuery } from "@tanstack/react-query";

import { CUSTOMER_SALES_SOURCE_ID } from "constants/config";

const serverErrorMessage =
  "Something went wrong at our end. Please try again later. You may reach out to our customer support team at 1800-570-6262 for further assistance.";

const createError = ({ status, message, errorCode }) => {
  const error = new Error(
    status >= 500
      ? serverErrorMessage
      : message || "An error occurred while processing the request.",
  );

  error.statusCode = status;
  error.errorCode = errorCode || null; // 👈 always present
  return error;
};

const orderModificationSummary = async (eventId) => {
  const response = await fetch(
    `${process.env.REACT_APP_ONE_OMS_URL}/customer-order-events/get-customer-event-details/${eventId}`,
    {
      method: "GET",
      headers: {
        accept: "application/json",
        "Content-Type": "application/json",
      },
    },
  );

  const data = await response.json().catch(() => {
    throw createError({ status: response.status });
  });

  if (!response.ok) {
    throw createError({
      status: response.status,
      message: data?.message,
      errorCode: data?.error_code ?? data?.errorCode,
    });
  }

  if (data?.status === "success") {
    return data.data;
  }

  throw createError({
    status: response.status,
    message: data?.message,
    errorCode: data?.error_code ?? data?.errorCode,
  });
};

export const useOrderModificationSummary = (eventId, options = {}) => {
  return useQuery({
    queryKey: ["order-modification-summary", eventId],
    queryFn: () => orderModificationSummary(eventId),
    enabled: !!eventId && options.enabled,
    ...options,
  });
};

const orderModificationDecision = async (payload) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/customer-order-events/update-order-modification-request`,
  );
  const headers = {
    "Content-Type": "application/json",
    "x-source-id": CUSTOMER_SALES_SOURCE_ID,
  };

  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.data;
  }
  const error = new Error(
    data?.message || "An error occurred while processing the request.",
  );
  error.statusCode = response.status;
  error.errorCode = data?.error_code;
  throw error;
};
export const useOrderModificationDecision = () => {
  return useMutation({
    mutationFn: (payload) => orderModificationDecision(payload),
  });
};

const fallBackOrderModificationSummary = async (eventId) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/customer-order-events/get-customer-event-details/${eventId}`,
  );
  const headers = {
    accept: "application/json",
    "Content-Type": "application/json",
  };
  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });
  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request.",
    );
  }
};

export const useFallBackOrderModificationSummary = (eventId) => {
  return useQuery({
    queryFn: () => fallBackOrderModificationSummary(eventId),
    enabled: !!eventId,
  });
};
